import { useState } from "react";
import { KeyRound, Loader2, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/utils/format";

import type { AccessKey } from "@/hooks/use-access-keys";

const DAY_MS = 86_400_000;
const EXPIRY_WARNING_DAYS = 14;

const SCOPE_LABELS: Record<AccessKey["scope_type"], string> = {
  platform: "Platform",
  external: "Webhook",
};

/** access-keys returns naive UTC timestamps; without a zone JS would read local time. */
function parseUtc(timestamp: string): Date {
  return new Date(
    /(Z|[+-]\d{2}:\d{2})$/.test(timestamp) ? timestamp : `${timestamp}Z`,
  );
}

interface AccessKeysTableProps {
  keys: AccessKey[];
  isLoading: boolean;
  /** Set while a delete runs; every delete is disabled until it settles. */
  deletingKeyId: string | null;
  onDelete: (key: AccessKey) => void;
}

export function AccessKeysTable({
  keys,
  isLoading,
  deletingKeyId,
  onDelete,
}: AccessKeysTableProps) {
  const [pendingDelete, setPendingDelete] = useState<AccessKey | null>(null);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-12 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Loading keys…
      </div>
    );
  }

  if (keys.length === 0) {
    return (
      <Empty className="py-12">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <KeyRound />
          </EmptyMedia>
          <EmptyTitle>No API keys</EmptyTitle>
          <EmptyDescription>
            Keys created here or from a tenant&apos;s own settings show up in
            this list.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Access</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="w-0" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key.access_key_id}>
              <TableCell>
                <div className="max-w-xs truncate font-medium" title={key.name}>
                  {key.name}
                </div>
                {key.description ? (
                  <div className="text-muted-foreground max-w-xs truncate text-xs">
                    {key.description}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="max-w-48 truncate">
                {key.created_by_name ?? key.created_by}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {key.roles.map((role) => (
                    <Badge
                      key={role}
                      variant={role === "Read" ? "secondary" : "info"}
                    >
                      {role}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{SCOPE_LABELS[key.scope_type]}</Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatDate(parseUtc(key.created_at))}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <ExpiryCell expireTime={key.expire_time} />
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete ${key.name}`}
                  title="Delete"
                  className="text-destructive"
                  disabled={deletingKeyId !== null}
                  onClick={() => {
                    setPendingDelete(key);
                  }}
                >
                  {deletingKeyId === key.access_key_id ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Trash2 className="size-4" aria-hidden />
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &quot;{pendingDelete?.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every integration using this key stops working immediately. This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => {
                if (pendingDelete) onDelete(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Delete key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ExpiryCell({ expireTime }: { expireTime: string | null }) {
  if (!expireTime) return <span>Never</span>;
  const expiresAt = parseUtc(expireTime);
  const msLeft = expiresAt.getTime() - Date.now();
  if (msLeft <= 0) {
    return <Badge variant="destructive">Expired {formatDate(expiresAt)}</Badge>;
  }
  const daysLeft = Math.ceil(msLeft / DAY_MS);
  if (daysLeft <= EXPIRY_WARNING_DAYS) {
    return (
      <Badge variant="warning">
        In {daysLeft}d · {formatDate(expiresAt)}
      </Badge>
    );
  }
  return <span>{formatDate(expiresAt)}</span>;
}
